import {useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token ? {'Authorization': `Bearer ${token}`} : {};
}

const DEBUG_TASK_DOC = true;
const taskDocCache = new Map();

/**
 * Hook to fetch a task document once from dash-api (no subscription).
 * Phase 6 replacement for Firestore `reports/{reportId}/tasks/{taskId}`.
 */
export function useTaskDoc(reportId, taskId) {
    const key = reportId && taskId ? `${reportId}:${taskId}` : null;

    const [state, setState] = useState(() => {
        if (key && taskDocCache.has(key)) {
            const cached = taskDocCache.get(key);
            return {data: cached.data, loading: false, notFound: cached.notFound};
        }
        return {data: null, loading: true, notFound: false};
    });

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!reportId || !taskId) {
                if (!cancelled) setState({data: null, loading: false, notFound: false});
                return;
            }
            const cacheKey = `${reportId}:${taskId}`;
            if (taskDocCache.has(cacheKey)) {
                const cached = taskDocCache.get(cacheKey);
                if (!cancelled) setState({data: cached.data, loading: false, notFound: cached.notFound});
                return;
            }
            if (!cancelled) setState({data: null, loading: true, notFound: false});

            try {
                const resp = await fetch(
                    `${BASE}/api/reports/${encodeURIComponent(reportId)}/tasks/${encodeURIComponent(taskId)}`,
                    {headers: authHeaders()}
                );
                if (resp.status === 404) {
                    taskDocCache.set(cacheKey, {data: null, notFound: true});
                    if (!cancelled) setState({data: null, loading: false, notFound: true});
                    return;
                }
                const data = resp.ok ? await resp.json() : null;
                const notFound = !data;
                if (DEBUG_TASK_DOC) console.log('[useTaskDoc] Received:', {reportId, taskId, data});
                taskDocCache.set(cacheKey, {data, notFound});
                if (!cancelled) setState({data, loading: false, notFound});
            } catch (error) {
                console.error('[useTaskDoc] fetch error:', {reportId, taskId, error});
                taskDocCache.set(cacheKey, {data: null, notFound: true});
                if (!cancelled) setState({data: null, loading: false, notFound: true});
            }
        }

        load();
        return () => { cancelled = true; };
    }, [reportId, taskId]);

    return state;
}
